/**
 * The layout's state machine: which widgets are open, where, and where chat
 * sits (v1.6). **Translated from v1's `frontend/src/lib/layout.js`, with its
 * tests** (`frontend.md` Q3 (a)); the rules are v1's, the types are new.
 * Class C: geometry of the screen, shared with no other client.
 *
 * Five named states fall out of the fields:
 *
 *     Empty            tiles=[null,null], chatSlot=null, expanded=null
 *     State 1 (left)   tiles=[A,null],    chatSlot=1,    expanded=null
 *     State 1 (right)  tiles=[null,A],    chatSlot=0,    expanded=null
 *     State 2          tiles=[A,B],       chatSlot=0|1,  expanded=null
 *     Expanded         tiles=[A,null],    chatSlot=null, expanded=0
 *
 * Folded (below `WIDE_THRESHOLD`) is one widget at a time over chat.
 *
 * **A tile's `id` is given once, when its widget opens, and kept through every
 * move** — swap, expand, fold. The shell keys its render on it, so a widget
 * that moves is the same component in a new place, and loses nothing (v1.6,
 * v1.82: a fold lost a journal draft).
 */

export const WIDE_THRESHOLD = 600;

export type Params = Record<string, unknown>;

/**
 * A widget that is now part of another one. An old address — a notification
 * not yet opened, an older reply's action — opens that one, and does not get
 * the tile that says a widget is unknown. Briefing is an item in Reading's
 * queue (the owner's list of 2026-10-12).
 */
const MOVED: Readonly<Record<string, string>> = { briefing: "reading" };

export const nowAt = (name: string): string => MOVED[name] ?? name;

export interface Tile {
  id: number;
  widget: string;
  params: Params | null;
  tab: string | null;
}

export type Slot = 0 | 1;
export type Tiles = [Tile | null, Tile | null];

export interface Layout {
  tiles: Tiles;
  /** The column chat sits in; null when it is centred (Empty, Expanded). */
  chatSlot: Slot | null;
  /** The expanded tile, which is always tile 0; null when none is. */
  expanded: Slot | null;
  isWide: boolean;
}

export type LayoutName = "folded" | "empty" | "state1" | "state2" | "expanded";

export function initialState({ isWide = false }: { isWide?: boolean } = {}): Layout {
  return { tiles: [null, null], chatSlot: null, expanded: null, isWide };
}

let nextTileId = 1;
function mkTile(widget: string, params: Params | null, tab: string | null): Tile {
  return { id: nextTileId++, widget, params, tab };
}

const other = (slot: Slot): Slot => (slot === 0 ? 1 : 0);

export interface Open {
  name: string;
  /** A column, `"expand"`, or unset for the automatic place. */
  tile?: Slot | "expand";
  params?: Params | null;
  tab?: string | null;
}

/**
 * Open a widget. Folded, it replaces the one widget. Unfolded and unset, the
 * first goes left; after that it lands in the empty column, or else in the
 * column chat is in, replacing what is there.
 */
export function openWidget(state: Layout, { name, tile, params = null, tab = null }: Open): Layout {
  const newTile = mkTile(name, params, tab);

  if (!state.isWide) {
    return { ...state, tiles: [newTile, null], chatSlot: null, expanded: null };
  }
  if (tile === "expand") {
    return { ...state, tiles: [newTile, null], chatSlot: null, expanded: 0 };
  }

  const wasEmpty = state.tiles[0] === null && state.tiles[1] === null;
  let target: Slot;
  if (tile === 0 || tile === 1) target = tile;
  else if (state.tiles[0] === null) target = 0;
  else if (state.tiles[1] === null) target = 1;
  else target = state.chatSlot ?? 1;

  // Opening from Expanded ends it. The expanded widget is in tile 0.
  if (state.expanded !== null) {
    if (target === 0) return { ...state, tiles: [newTile, null], chatSlot: 1, expanded: null };
    return { ...state, tiles: [state.tiles[0], newTile], chatSlot: 1, expanded: null };
  }

  const tiles: Tiles = [...state.tiles];
  tiles[target] = newTile;
  // From Empty, chat takes the other column whole. After that, the new widget
  // lands where chat is, and chat shares that column with it.
  const chatSlot = wasEmpty ? other(target) : target;
  return { ...state, tiles, chatSlot, expanded: null };
}

/**
 * Close the widget in `tile`. The other stays where it is and chat moves into
 * the column it leaves. Closing the expanded widget returns to Empty: expand is
 * a one-way commitment.
 */
export function closeWidget(state: Layout, { tile }: { tile: Slot }): Layout {
  if (!state.isWide) {
    if (state.tiles[0] === null) return state;
    return { ...state, tiles: [null, null], chatSlot: null, expanded: null };
  }
  if (state.expanded !== null) {
    if (tile !== state.expanded) return state;
    return { ...state, tiles: [null, null], chatSlot: null, expanded: null };
  }
  if (state.tiles[tile] === null) return state;

  const tiles: Tiles = [...state.tiles];
  tiles[tile] = null;
  if (tiles[0] === null && tiles[1] === null) {
    return { ...state, tiles, chatSlot: null, expanded: null };
  }
  return { ...state, tiles, chatSlot: tile, expanded: null };
}

/** Expand a widget to the full width; from State 2 the other one closes. */
export function expandWidget(state: Layout, { tile }: { tile: Slot }): Layout {
  if (!state.isWide) return state;
  const chosen = state.tiles[tile];
  if (chosen === null) return state;
  return { ...state, tiles: [chosen, null], chatSlot: null, expanded: 0 };
}

/** Back to State 1 (left), with no memory of a State 2 before it. */
export function collapseExpanded(state: Layout): Layout {
  if (state.expanded === null) return state;
  return { ...state, tiles: [state.tiles[0], null], chatSlot: 1, expanded: null };
}

/** Mirror State 2 left and right; chat follows the widget it was with. */
export function swapTiles(state: Layout): Layout {
  if (state.expanded !== null) return state;
  if (state.tiles[0] === null || state.tiles[1] === null) return state;
  return {
    ...state,
    tiles: [state.tiles[1], state.tiles[0]],
    chatSlot: state.chatSlot === null ? null : other(state.chatSlot),
  };
}

/**
 * The fold. Unfolding makes the folded widget Expanded; folding keeps the
 * expanded widget, else the left, else the right.
 */
export function setIsWide(state: Layout, isWide: boolean): Layout {
  if (state.isWide === isWide) return state;
  if (isWide) {
    const folded = state.tiles[0];
    if (folded === null) {
      return { ...state, isWide, tiles: [null, null], chatSlot: null, expanded: null };
    }
    return { ...state, isWide, tiles: [folded, null], chatSlot: null, expanded: 0 };
  }
  const survivor =
    state.expanded !== null ? state.tiles[state.expanded] : (state.tiles[0] ?? state.tiles[1]);
  return { ...state, isWide, tiles: [survivor, null], chatSlot: null, expanded: null };
}

/**
 * The tile the agent takes as the one in front: the expanded one; else the one
 * beside chat (the last opened); else the only one.
 */
export function focusedTileIndex(state: Layout): Slot | null {
  if (state.expanded !== null) return state.expanded;
  if (state.chatSlot !== null && state.tiles[state.chatSlot] !== null) return state.chatSlot;
  if (state.tiles[0] !== null) return 0;
  if (state.tiles[1] !== null) return 1;
  return null;
}

export function focusedTile(state: Layout): Tile | null {
  const index = focusedTileIndex(state);
  return index === null ? null : state.tiles[index];
}

/** Change one tile's params or tab, and nothing else. */
export function patchTile(
  state: Layout,
  index: Slot | null,
  patch: Partial<Pick<Tile, "params" | "tab">>,
): Layout {
  if (index === null) return state;
  const tile = state.tiles[index];
  if (tile === null) return state;
  const next = { ...tile, ...patch };
  if (next.params === tile.params && next.tab === tile.tab) return state;
  const tiles: Tiles = [...state.tiles];
  tiles[index] = next;
  return { ...state, tiles };
}

export function patchFocusedTile(
  state: Layout,
  patch: Partial<Pick<Tile, "params" | "tab">>,
): Layout {
  return patchTile(state, focusedTileIndex(state), patch);
}

export function layoutName(state: Layout): LayoutName {
  if (!state.isWide) return "folded";
  if (state.expanded !== null) return "expanded";
  const [a, b] = state.tiles;
  if (a === null && b === null) return "empty";
  if (a !== null && b !== null) return "state2";
  return "state1";
}
